import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { COVERAGE_STATES, type CoveragePolicy, type Scenario } from "@ti/core";
import { ArrowLeft, ChevronRight, Plus, ScrollText } from "lucide-react";
import { useMemo, useState } from "react";
import { AgentLink, PageHeader } from "@/components/domain/misc";
import { StatTile } from "@/components/domain/stat";
import { COVERAGE_COLOR, CoverageChip } from "@/components/domain/status";
import { ErrorPanel, Page, QueryState } from "@/components/layout/page";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { EmptyState, Meter, Skeleton } from "@/components/ui/misc";
import { MAGNITUDE_COLOR } from "@/features/agents/bits";
import { ClaimSheet, ClaimsTable } from "@/features/coverage/claims-table";
import { EndorsementList } from "@/features/coverage/endorsement-timeline";
import { FileClaimDialog } from "@/features/coverage/file-claim-dialog";
import { LimitHistoryChart } from "@/features/coverage/limit-history-chart";
import { GRADUATED_SCHEDULE, MODE_LABEL, STAGES, STATE_DESCRIPTION, graduatedStepLabel } from "@/features/coverage/meta";
import { Fact, LossTypeChips, PolicyStatusBadge, TermsBadge, countByState, daysRemaining, lossTypesOf, policyColors } from "@/features/coverage/policy-bits";
import { ProjectionCard, scopeForPolicy } from "@/features/coverage/projection-card";
import { ScenarioSheet, ScenarioTable, sortScenarios } from "@/features/coverage/scenario-table";
import { ApiError, q } from "@/lib/api";
import { fmtDate, fmtMoney, fmtPct, fmtShortDate } from "@/lib/format";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/coverage/policies/$policyId")({
  component: PolicyDetailPage,
});

function Breadcrumb({ name }: { name?: string }) {
  return (
    <nav aria-label="Breadcrumb">
      <Link to="/coverage" className="hover:text-fg">
        Policies & coverage
      </Link>
      <span className="px-1.5">/</span>
      <span className="text-fg-2">{name ?? "Policy"}</span>
    </nav>
  );
}

function PolicyDetailPage() {
  const { policyId } = Route.useParams();
  const policy = useQuery(q.policy(policyId));

  if (policy.error) {
    const notFound = policy.error instanceof ApiError && policy.error.status === 404;
    return (
      <Page>
        <Link to="/coverage" className="mb-3 inline-flex items-center gap-1 text-xs text-fg-3 hover:text-fg">
          <ArrowLeft className="size-3" /> All policies
        </Link>
        {notFound ? (
          <Card>
            <EmptyState icon={<ScrollText />} title="Policy not found" description={`No policy with ID ${policyId} exists on this account.`} />
          </Card>
        ) : (
          <ErrorPanel error={policy.error} reset={() => void policy.refetch()} />
        )}
      </Page>
    );
  }

  return (
    <Page>
      {policy.data ? (
        <PolicyDetail policy={policy.data} />
      ) : (
        <>
          <Skeleton className="mb-2 h-3 w-48" />
          <Skeleton className="mb-2 h-6 w-80" />
          <Skeleton className="mb-5 h-4 w-[32rem]" />
          <Skeleton className="mb-4 h-[62px]" />
          <div className="mb-4 grid grid-cols-4 gap-3">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
          <Skeleton className="h-80" />
        </>
      )}
    </Page>
  );
}

function PolicyDetail({ policy: p }: { policy: CoveragePolicy }) {
  const policies = useQuery(q.policies());
  const agents = useQuery(q.agents());
  const scenarios = useQuery(q.scenarios({ policyId: p.id }));
  const allEndorsements = useQuery(q.endorsements());
  const claims = useQuery(q.claims({ policyId: p.id }));
  const [scenarioId, setScenarioId] = useState<string | null>(null);
  const [claimId, setClaimId] = useState<string | null>(null);
  const [filing, setFiling] = useState(false);

  const color = policyColors(policies.data).get(p.id);
  const endorsements = useMemo(() => (allEndorsements.data ?? []).filter((e) => e.policyId === p.id).sort((a, b) => b.seq - a.seq), [allEndorsements.data, p.id]);
  const sorted = useMemo(() => (scenarios.data ? sortScenarios(scenarios.data) : undefined), [scenarios.data]);
  const scope = agents.data ? scopeForPolicy(p, agents.data) : null;
  const agentName = (id: string) => agents.data?.find((a) => a.id === id)?.name ?? id;
  const stage = STAGES.find((s) => s.id === p.stage);
  const remaining = Math.max(0, p.aggregateLimit.amount - p.used.amount);
  const left = daysRemaining(p);

  return (
    <div className="flex flex-col gap-4">
      <div>
        <PageHeader
          className="pb-4"
          breadcrumb={<Breadcrumb name={p.name} />}
          title={
            <span className="flex flex-wrap items-center gap-2.5">
              <span className="size-3 shrink-0 rounded-sm" style={{ background: color }} aria-hidden />
              {p.name}
              <PolicyStatusBadge status={p.status} />
              <TermsBadge mode={p.mode} />
            </span>
          }
          description={
            <>
              {p.program} · {p.carrier} · policy <span className="font-mono text-[12px] text-fg-2">{p.policyNumber}</span>
            </>
          }
          actions={
            <>
              <Button variant="outline" size="sm" asChild>
                <Link to="/coverage/scenarios" search={{ policyId: p.id }}>
                  Scenario registry
                </Link>
              </Button>
              <Button size="sm" onClick={() => setFiling(true)} disabled={p.status !== "active" && p.status !== "expired"}>
                <Plus /> File a claim
              </Button>
            </>
          }
        />
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 rounded-lg border bg-surface px-4 py-3 md:grid-cols-3 xl:grid-cols-[auto_auto_auto_auto_auto_minmax(0,1fr)]">
          <Fact label="Carrier">{p.carrier}</Fact>
          <Fact label="Period">
            <span className="tabular">
              {fmtDate(p.periodStart)} – {fmtDate(p.periodEnd)}
            </span>
            <span className="text-xs text-fg-3 tabular">· {p.status === "expired" ? "ended" : `${left} days left`}</span>
          </Fact>
          <Fact label="Terms">
            {MODE_LABEL[p.mode].label}
            <span className="text-xs text-fg-3">{p.mode === "graduated" ? "· step-down after grace" : "· excluded after grace"}</span>
          </Fact>
          <Fact label="Loss types">
            {scenarios.data ? <LossTypeChips types={lossTypesOf(scenarios.data)} /> : <Skeleton className="h-4 w-32" />}
          </Fact>
          <Fact label="Program stage">{stage ? `Stage ${stage.step} · ${stage.label}` : p.stage}</Fact>
          <Fact label={`Agents covered (${p.agentIds.length})`}>
            {p.agentIds.map((id) => (
              <AgentLink key={id} id={id} name={agentName(id)} className="text-[12px]" />
            ))}
          </Fact>
        </dl>
      </div>

      <section aria-label="Limits and premium" className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile
          label="Aggregate limit"
          value={fmtMoney(p.aggregateLimit)}
          sub={`${fmtMoney(p.used)} used (${fmtPct(p.aggregateLimit.amount ? p.used.amount / p.aggregateLimit.amount : 0, 1)}) · ${fmtMoney({ amount: remaining, currency: p.aggregateLimit.currency })} remaining`}
          footer={<Meter value={p.used.amount} max={p.aggregateLimit.amount || 1} tone={MAGNITUDE_COLOR} label="Aggregate limit used" />}
        />
        <StatTile label="Per-claim limit" value={fmtMoney(p.perClaimLimit)} sub="Most the policy pays on a single claim; scenario sub-limits also apply" />
        <StatTile label="Deductible" value={fmtMoney(p.deductible)} sub="Per claim, borne by you before the carrier pays" />
        <StatTile
          label="Premium"
          value={
            <>
              {fmtMoney(p.premium.annual)}
              <span className="ml-1 text-sm font-normal text-fg-3">/yr</span>
            </>
          }
          sub={`${fmtMoney(p.premium.monthly)} per month · ${p.scenarioCount} ${p.scenarioCount === 1 ? "scenario" : "scenarios"}`}
        />
      </section>

      <div className="grid gap-4 lg:grid-cols-12">
        <Card className="flex flex-col lg:col-span-7">
          <CardHeader title="Limit history" description="Aggregate limit for this policy. Markers are endorsements; increases apply at most monthly and only ratchet up mid-term." />
          <CardBody className="flex flex-1 flex-col justify-between gap-3 pb-3">
            <LimitHistoryChart policy={p} endorsements={allEndorsements.data} height={260} color={color} />
            <LimitStats policy={p} />
          </CardBody>
        </Card>
        <Card className="lg:col-span-5">
          <CardHeader
            title="Scenarios by state"
            description="Cover is granted per scenario: an action class within a risk envelope."
            actions={
              <Link to="/coverage/scenarios" search={{ policyId: p.id }} className="text-xs text-fg-3 hover:text-accent">
                Registry
              </Link>
            }
          />
          <CardBody>
            <QueryState query={scenarios} loading={<Skeleton className="h-64" />}>
              {(list) => <StateSummary policyId={p.id} scenarios={list} />}
            </QueryState>
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader
          title="Scenarios"
          description="Sub-limits, per-claim limits and the issue behind each state. Select a row for its endorsement history."
          actions={<span className="text-xs text-fg-3 tabular">{sorted?.length ?? "—"}</span>}
        />
        {scenarios.error ? (
          <CardBody>
            <ErrorPanel error={scenarios.error} reset={() => void scenarios.refetch()} />
          </CardBody>
        ) : (
          <ScenarioTable
            scenarios={sorted}
            loading={scenarios.isPending}
            endorsements={allEndorsements.data}
            showPolicy={false}
            onSelect={(s) => setScenarioId(s.id)}
            empty={{ title: "No scenarios on this policy", description: "Scenarios are added when the policy is bound or by endorsement." }}
          />
        )}
      </Card>

      {scope ? <ProjectionCard policy={p} scope={scope} /> : agents.isPending ? <Skeleton className="h-[380px]" /> : null}

      <Card>
        <CardHeader
          title="Claims"
          description="Claims against this policy — first-party from your team, third-party from people your agents affected (via the protection page), or opened automatically from detected losses."
          actions={
            <Button size="sm" variant="outline" onClick={() => setFiling(true)} disabled={p.status !== "active" && p.status !== "expired"}>
              <Plus /> File a claim
            </Button>
          }
        />
        {claims.error ? (
          <CardBody>
            <ErrorPanel error={claims.error} reset={() => void claims.refetch()} />
          </CardBody>
        ) : (
          <ClaimsTable
            claims={claims.data}
            loading={claims.isPending}
            showPolicy={false}
            onSelect={(c) => setClaimId(c.id)}
            empty={{ title: "No claims on this policy", description: "Claims filed by your team, by people your agents affected, or prompted from detected losses appear here." }}
          />
        )}
      </Card>

      <div className="grid gap-4 lg:grid-cols-12">
        <Card className="lg:col-span-7">
          <CardHeader
            title="Endorsements"
            description="Every change to this policy is an immutable, hash-chained endorsement."
            actions={
              <Link to="/coverage/endorsements" search={{ policyId: p.id }} className="text-xs text-fg-3 hover:text-accent">
                Full chain
              </Link>
            }
          />
          <CardBody>
            {allEndorsements.isPending ? (
              <Skeleton className="h-64" />
            ) : endorsements.length ? (
              <EndorsementList items={endorsements} limit={8} />
            ) : (
              <EmptyState className="py-6" title="No endorsements yet" />
            )}
            {endorsements.length > 8 ? (
              <Link to="/coverage/endorsements" search={{ policyId: p.id }} className="mt-3 inline-flex items-center gap-1 text-xs text-fg-3 hover:text-accent">
                {endorsements.length - 8} earlier endorsements <ChevronRight className="size-3" />
              </Link>
            ) : null}
          </CardBody>
        </Card>
        <GraduatedScheduleCard className="lg:col-span-5" policy={p} scenarios={scenarios.data} loading={scenarios.isPending} />
      </div>

      <ScenarioSheet scenario={sorted?.find((s) => s.id === scenarioId) ?? null} endorsements={allEndorsements.data} onClose={() => setScenarioId(null)} />
      <ClaimSheet claim={claims.data?.find((c) => c.id === claimId) ?? null} onClose={() => setClaimId(null)} />
      <FileClaimDialog open={filing} onOpenChange={setFiling} defaultPolicyId={p.id} onFiled={(c) => setClaimId(c.id)} />
    </div>
  );
}

function LimitStats({ policy: p }: { policy: CoveragePolicy }) {
  const h = p.limitHistory;
  const first = h[0];
  const last = h.at(-1);
  const increases = h.filter((x, i) => i > 0 && x.limit > (h[i - 1]?.limit ?? 0)).length;
  const lastEndorsed = [...h].reverse().find((x) => x.endorsementId);
  const nextReview = lastEndorsed ? new Date(Math.max(Date.parse(lastEndorsed.date) + 30 * 86_400_000, Date.now())).toISOString() : null;
  const money = (n: number) => fmtMoney({ amount: n, currency: p.aggregateLimit.currency });
  const items: Array<[string, string]> = [
    ["Bound at", first ? money(first.limit) : "—"],
    ["Endorsed increases", String(increases)],
    ["Growth since bind", first && last && first.limit > 0 ? `${(last.limit / first.limit).toFixed(1)}×` : "—"],
    ["Trajectory", p.projectedLimit.amount > p.aggregateLimit.amount ? `${fmtMoney(p.projectedLimit, { compact: true })} by ${fmtShortDate(p.projectedAt)}` : "Holding"],
    ["Next monthly review", nextReview ? fmtDate(nextReview) : "—"],
  ];
  return (
    <dl className="grid grid-cols-2 gap-3 border-t pt-3 sm:grid-cols-5">
      {items.map(([k, v]) => (
        <div key={k} className="min-w-0">
          <dt className="text-[11px] text-fg-3">{k}</dt>
          <dd className="mt-0.5 truncate text-[13px] font-semibold tabular" title={v}>
            {v}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function StateSummary({ policyId, scenarios }: { policyId: string; scenarios: Scenario[] }) {
  const counts = countByState(scenarios);
  const total = scenarios.length || 1;
  const protectedCount = counts.covered + counts.grace + counts.mitigated + counts.verifying;
  const subLimits = scenarios.reduce((n, s) => n + s.subLimit.amount, 0);
  return (
    <div className="flex flex-col gap-3">
      <div>
        <div className="text-xs text-fg-3">
          <span className="text-[15px] font-semibold text-fg tabular">{protectedCount}</span> of {scenarios.length} scenarios covered, in grace, mitigated or verifying ·{" "}
          {fmtMoney(subLimits, { compact: true })} in sub-limits
        </div>
        <div className="mt-2 flex h-2 w-full gap-0.5 overflow-hidden rounded-full" role="img" aria-label={COVERAGE_STATES.map((s) => `${counts[s]} ${s}`).join(", ")}>
          {COVERAGE_STATES.filter((s) => counts[s] > 0).map((s) => (
            <span key={s} style={{ width: `${(counts[s] / total) * 100}%`, background: COVERAGE_COLOR[s] }} />
          ))}
        </div>
      </div>
      <ul className="-mx-2 flex flex-col">
        {COVERAGE_STATES.map((s) => (
          <li key={s}>
            <Link
              to="/coverage/scenarios"
              search={{ policyId, state: s }}
              className={cn("group flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-surface-2", counts[s] === 0 && "opacity-60")}
              title={STATE_DESCRIPTION[s]}
            >
              <CoverageChip state={s} withHelp={false} />
              <span className="min-w-0 flex-1 truncate text-xs text-fg-3">{STATE_DESCRIPTION[s].split(".")[0]}</span>
              <span className="w-6 text-right text-[13px] font-semibold tabular">{counts[s]}</span>
              <ChevronRight className="size-3.5 text-fg-3 opacity-0 group-hover:opacity-100" aria-hidden />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function GraduatedScheduleCard({ policy, scenarios, loading, className }: { policy: CoveragePolicy; scenarios: Scenario[] | undefined; loading: boolean; className?: string }) {
  const strict = policy.mode === "strict";
  const atRow = (rowId: string, coverage: number | null, coinsurance: number | null) => {
    if (!scenarios) return [];
    if (rowId === "grace") return scenarios.filter((s) => s.state === "grace");
    if (rowId === "excluded") return scenarios.filter((s) => s.state === "excluded");
    return scenarios.filter((s) => s.state === "graduated" && s.graduatedStage?.coverage === coverage && s.graduatedStage?.coinsurance === coinsurance);
  };
  const rows = strict ? GRADUATED_SCHEDULE.filter((r) => r.id === "grace" || r.id === "excluded") : GRADUATED_SCHEDULE;

  return (
    <Card className={className}>
      <CardHeader
        title={strict ? "Strict terms" : "Graduated schedule"}
        description={
          strict
            ? "When an issue isn't remediated by its grace deadline, the scenario is excluded until the fix is verified."
            : "When an issue isn't remediated by its grace deadline, the scenario's sub-limit steps down with co-insurance before any exclusion. Each step is an endorsement."
        }
      />
      <table className="w-full text-[13px]">
        <caption className="sr-only">{strict ? "Strict coverage terms" : "Graduated coverage schedule"} and the scenarios currently on each step</caption>
        <thead>
          <tr className="border-b text-[11px] uppercase tracking-wide text-fg-3">
            <th scope="col" className="px-4 py-2 text-left font-medium">
              After deadline
            </th>
            <th scope="col" className="px-2 py-2 text-right font-medium">
              Sub-limit
            </th>
            <th scope="col" className="px-2 py-2 text-right font-medium">
              Co-ins.
            </th>
            <th scope="col" className="px-4 py-2 text-left font-medium">
              On this step
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const here = atRow(row.id, row.coverage, row.coinsurance);
            const stepped = row.id !== "grace" && here.length > 0;
            const when = strict && row.id === "excluded" ? "Deadline passed" : strict ? "Until deadline" : row.window;
            return (
              <tr key={row.id} className={cn("border-b last:border-0", stepped && "bg-serious/[0.06]")}>
                <td className={cn("px-4 py-2.5 font-medium", stepped && "border-l-2 border-l-serious")}>
                  {when}
                  {row.id !== "grace" && row.id !== "excluded" ? <span className="block text-[11px] font-normal text-fg-3">{row.note}</span> : null}
                </td>
                <td className="px-2 py-2.5 text-right tabular">{row.coverage === null ? "—" : fmtPct(row.coverage)}</td>
                <td className="px-2 py-2.5 text-right tabular">{row.coinsurance === null ? "—" : row.coinsurance === 0 ? "None" : fmtPct(row.coinsurance)}</td>
                <td className="px-4 py-2.5">
                  {loading ? (
                    <Skeleton className="h-4 w-28" />
                  ) : here.length === 0 ? (
                    row.coverage === null ? (
                      <CoverageChip state="excluded" withHelp={false} />
                    ) : (
                      <span className="text-xs text-fg-3">None</span>
                    )
                  ) : (
                    <div className="flex flex-col items-start gap-1">
                      <CoverageChip state={row.id === "grace" ? "grace" : row.id === "excluded" ? "excluded" : "graduated"} withHelp={false} />
                      {here.map((s) => (
                        <span key={s.id} className="font-mono text-[11.5px] text-fg" title={s.graduatedStage ? `${graduatedStepLabel(s.graduatedStage)} · ${s.agentName}` : s.agentName}>
                          {s.key}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="border-t px-4 py-2.5 text-[11px] text-fg-3">Critical issues are covered at 50% of the sub-limit during grace. Grace windows: critical 48 h, high 7 days, medium 14 days, low 30 days.</p>
    </Card>
  );
}

